import { EventCacheAPI } from './EventCacheAPI';
import { EventDatesMixin } from './EventDatesMixin';

export const EventAPI = {
    mixins: [EventCacheAPI, EventDatesMixin],

    mounted() {
        this.eventBus.$on('store-event', this.storeEvent);
        this.eventBus.$on('update-event', this.updateEvent);
        this.eventBus.$on('reschedule-event', this.rescheduleEvent);
        this.eventBus.$on('delete-event-confirm', this.deleteEvent);
    },

    data() {
        return {
            lastRequestedEventsStartDate: null,
            lastRequestedEventsEndDate: null,
        }
    },

    methods: {
        getEvents(options) {
            if (!this.eventsUrl) return Promise.resolve();

            if (this.viewStartDate && this.viewEndDate && !window.force_all_events) return this.getEventsBetweenDates(options);

            // Verification code is used to know with which response to populate data events
            let verificationCode = this.startRequest('get-events');
            this.loading(true);

            return axios
                .get(this.eventsUrl)
                .then(response => {
                    // Check if this is the last request
                    if (!this.isValidRequest('get-events', verificationCode)) return;

                    this.loading(false);

                    this.events = response.data.events;

                    if (response.data.event_templates) {
                        this.eventTemplates = response.data.event_templates;
                    }

                    this.fixEventsData();
                });
        },

        getEventsBetweenDates(options = {}, cb) {
            if (!this.eventsUrl) return Promise.resolve();

            if (!this.viewStartDate || !this.viewEndDate || window.force_all_events) return this.getEvents();

            // Verification code is used to know with which response to populate data events
            let verificationCode = this.startRequest('get-events');
            this.loading(true);

            if (this.view === 'month' || this.view === 'free_places') {
                this.lastRequestedEventsStartDate = moment(this.viewStartDate);
                this.lastRequestedEventsEndDate = moment(this.viewEndDate);
            } else {
                this.lastRequestedEventsStartDate = moment(this.viewStartDate)
                    .add(-this.viewNumberOfDays, 'days');
                this.lastRequestedEventsEndDate = moment(this.viewEndDate)
                    .add(2 * this.viewNumberOfDays, 'days');
            }

            return axios
                .get(this.eventsUrl, {
                    params: {
                        start_date: this.lastRequestedEventsStartDate.toISOString(),
                        end_date: this.lastRequestedEventsEndDate.toISOString(),
                        ...options.query
                    }
                })
                .then(response => {
                    // Check if this is the last request
                    if (!this.isValidRequest('get-events', verificationCode)) return;

                    this.loading(false);

                    if (cb) {
                        cb(response.data.events, response.data.event_templates);
                    } else {
                        this.events = response.data.events;

                        if (response.data.event_templates) {
                            this.eventTemplates = response.data.event_templates;
                        }
                    }

                    this.fixEventsData();
                });
        },

        storeEvent(event, cb) {
            // Callers keep their dialog open until cb fires, so bailing out without
            // calling it would leave them waiting forever.
            if (!this.storeEventsUrl) {
                // The grid should not have offered a modal in this state, but if
                // something still gets here the user must see why nothing happened.
                this.$notify({ group: 'app', type: 'error', text: this.texts.select_calendar_to_create });
                cb && cb(new Error('Store events url is empty.'));
                return Promise.resolve();
            }

            this.loading(true);

            return axios
                .post(this.storeEventsUrl, event)
                .then(response => {
                    console.log("OK", response);
                    this.loading(false);

                    this.eventBus.$emit('created-event', response.data.event || response.data.event_template);

                    cb && cb();

                    this.$notify({ group: 'app', text: response.data.message, type: 'success' });
                }, error => {
                    console.log("ERROR", error.response);
                    this.loading(false);

                    cb && cb(error);

                    this.$notify({ group: 'app', text: error?.response?.data?.message || 'A apărut o eroare. Încercați din nou.', type: 'error' });

                    // _LTracker.push({
                    //     'action': 'Store Event Error',
                    //     'error': error
                    // });
                });
        },

        updateEvent(event, cb) {
            // See storeEvent: cb must fire on every path.
            if (!this.updateEventsUrl) {
                cb && cb(new Error('Update events url is empty.'));
                return Promise.resolve();
            }

            // Fix Converting circular structure to JSON. Remove internal properties starting with underscore.
            let _event = {};
            for (let key in event) {
                if (!key.startsWith('_')) {
                    _event[key] = event[key];
                }
            }

            this.loading(true);

            return axios
                .post(this.updateEventsUrl, _event)
                .then(response => {
                    console.log("OK", response);
                    this.loading(false);

                    this.eventBus.$emit('updated-event', response.data.event || response.data.event_template);

                    cb && cb();

                    this.$notify({ group: 'app', text: response.data.message, type: 'success' });
                }, error => {
                    console.log("ERROR", error);
                    this.loading(false);

                    cb && cb(error);

                    this.$notify({ group: 'app', text: error?.response?.data?.message || 'A apărut o eroare. Încercați din nou.', type: 'error' });

                    // _LTracker.push({
                    //     'action': 'Update Event Error',
                    //     'error': error
                    // });
                });
        },

        rescheduleEvent(event, cb) {
            const url = this.rescheduleEventsUrl || this.updateEventsUrl;
            if (!url) return Promise.resolve();

            // Strip internal (_-prefixed) keys to avoid circular JSON.
            let _event = {};
            for (let key in event) {
                if (!key.startsWith('_')) _event[key] = event[key];
            }

            this.loading(true);

            return axios.post(url, _event).then(response => {
                this.loading(false);
                this.eventBus.$emit('updated-event', response.data.event || response.data.event_template);
                cb && cb();
                this.$notify({ group: 'app', text: response.data.message, type: 'success' });
            }, error => {
                this.loading(false);
                cb && cb(error);
                this.$notify({ group: 'app', text: error?.response?.data?.message || 'A apărut o eroare. Încercați din nou.', type: 'error' });
            });
        },

        deleteEvent(event, date, deleteType = 'this-event', confirmExamRisk = false) {
            if (!event.delete_url && !this.deleteEventsUrl) return Promise.resolve();// this.$notify({group: 'app', text: 'Timetable component not initialised correctly! Delete events url is empty.', type: 'error'});

            this.loading(true);

            const requestData = {
                event_id: event.id,
                date: moment(date).format(),
                'delete-type': deleteType,
                confirm_exam_risk: !!confirmExamRisk,
            };

            return axios
                .request({
                    url: event.delete_url || this.deleteEventsUrl,
                    method: 'delete',
                    params: requestData,
                    data: requestData,
                })
                .then(response => {
                    console.log("DELETE OK", response);
                    this.loading(false);

                    if (response.data.requires_confirmation) {
                        this.eventBus.$emit('delete-event-requires-confirmation', event, date, deleteType, response.data);

                        return response;
                    }

                    this.eventBus.$emit('deleted-event', event, date, response.data);

                    this.$notify({ group: 'app', text: response.data.message, type: 'warning' });
                }, error => {
                    console.log("DELETE ERROR", error.response);
                    this.loading(false);

                    this.eventBus.$emit('delete-event-failed', event, date, error);

                    this.$notify({ group: 'app', text: error?.response?.data?.message || 'A apărut o eroare. Încercați din nou.', type: 'error' });

                    // _LTracker.push({
                    //     'action': 'Delete Event Error',
                    //     'error': error
                    // });
                });
        }
    },

    computed: {
        canCacheEvents() {
            return this.viewStartDate && this.viewEndDate;
        }
    }
};
